import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconsComponent } from '@sml/icons';
import { ISelectedRange } from '../src/calendar.interface';
import { ButtonsModule } from '@sml/buttons';
import { TooltipDirective } from "@sml/utility";

@Component({
  selector: 'sml-range-picker',
  standalone: true,
  imports: [CommonModule, IconsComponent, ButtonsModule, TooltipDirective],
  templateUrl: './range-picker.component.html',
  styleUrl: './range-picker.component.scss',
})
export class RangePickerComponent implements OnInit, AfterViewInit {

  private readonly cdr = inject(ChangeDetectorRef);
  // COMMON VARIABLES
  @Input() hasSingleCalndar = false;
  @Input() showClearButton = true;
  @Input() showApplyButton = true;
  @Input() showSelectedDates = true;
  @Input() currentMonth!: Date;
  @Input() nextMonth!: Date;
  @Input() daysOfWeek: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // RANGE DATE SELECTION
  @Input() selectedRange: ISelectedRange = { startDate: null, endDate: null };
  @Output() selectedDates = new EventEmitter<ISelectedRange>();

  // SINGLE DATE SELECTION
  @Input() hasSingleDatePicker = false;
  @Input() selectedDate: Date | null = null;
  @Output() selectedDateChange = new EventEmitter<Date | null>();

  // YEAR SELECTION VARIABLES
  @Input() showYearPicker = false;
  @Input() calendarYear!: number;
  @Input() nextCalStartYear!: number;
  @Input() disablePastDates = false;

  tooltipText = '';


  ngOnInit(): void {
    this.initializeMonths();
  }
  ngAfterViewInit(): void {
    this.updateCalendarForSingleDatePicker();
    this.updateCalendarForRangePicker();
  }

  private updateCalendarForSingleDatePicker(): void {
    if (this.hasSingleCalndar && this.hasSingleDatePicker && this.selectedDate) {
      const selectedYear = this.selectedDate.getFullYear();
      const selectedMonth = this.selectedDate.getMonth();
      this.calendarYear = selectedYear;
      this.currentMonth = new Date(selectedYear, selectedMonth, 1);
      this.nextMonth = new Date(selectedYear, selectedMonth + 1, 1);
      this.cdr.detectChanges();
    }
  }

  private updateCalendarForRangePicker(): void {
    if (!this.hasSingleCalndar && !this.hasSingleDatePicker && this.selectedRange.startDate) {
      const selectedYear = this.selectedRange.startDate.getFullYear();
      const selectedMonth = this.selectedRange.startDate.getMonth();
      this.calendarYear = selectedYear;
      this.currentMonth = new Date(selectedYear, selectedMonth, 1);
      this.nextMonth = new Date(selectedYear, selectedMonth + 1, 1);
      this.cdr.detectChanges();
    }
  }

  initializeMonths(): void {
    const now = new Date();
    this.calendarYear = now.getFullYear();
    this.nextCalStartYear = now.getFullYear() + 12;
    this.currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    this.nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  generateYearList(year: number): number[] {
    const startYear = year;
    const endYear = year + 11;
    let years = [];
    if (this.hasSingleDatePicker) {
      years = Array.from({ length: 12 }, (_, i) => startYear + i);
    } else {
      years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(year);
      }
    }
    return years;
  }

  toggleYearPicker(): void {
    this.showYearPicker = !this.showYearPicker;
  }

  selectYear(year: number): void {
    this.calendarYear = year;
    this.currentMonth = new Date(year, this.currentMonth.getMonth(), 1);
    const nextMonthYear = this.currentMonth.getMonth() === 11 ? year + 1 : year;
    this.nextMonth = new Date(nextMonthYear, (this.currentMonth.getMonth() + 1) % 12, 1);
    this.showYearPicker = false;
    this.clearDates();
  }

  generateDays(month: Date): Date[] {
    const days: Date[] = [];
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(new Date(year, monthIndex, i - firstDay.getDay() + 1));
    }
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, monthIndex, day));
    }
    return days;
  }

  isWithinRange(day: Date): boolean {
    const { startDate, endDate } = this.selectedRange;
    return (
      startDate !== null &&
      endDate !== null &&
      day >= new Date(startDate.setHours(0, 0, 0, 0)) &&
      day <= new Date(endDate.setHours(0, 0, 0, 0))
    ) || false;
  }

  isSelected(day: Date): boolean {
    const { startDate, endDate } = this.selectedRange;
    if (this.hasSingleDatePicker) {
      return !!(
        this.selectedDate && day.toDateString() === this.selectedDate.toDateString()
      );
    }
    return !!(
      (startDate && day.toDateString() === startDate.toDateString()) ||
      (endDate && day.toDateString() === endDate.toDateString())
    );
  }

  selectDate(day: Date): void {
    const { startDate, endDate } = this.selectedRange;
    if (this.hasSingleDatePicker) {
      this.selectedDate = day;
      if (!this.showApplyButton) {
        this.selectedDateChange.emit(this.selectedDate);
      }
    } else {
      if (!startDate || (startDate && endDate)) {
        this.selectedRange = { startDate: day, endDate: null };
      } else if (day >= startDate) {
        this.selectedRange.endDate = day;
      } else {
        this.selectedRange = { startDate: day, endDate: null };
      }
    }
  }


  // selectDate(day: Date): void {
  //   const { startDate, endDate } = this.selectedRange;
  //   if (!startDate || (startDate && endDate)) {
  //     this.selectedRange = { startDate: day, endDate: null };
  //   } else if (day >= startDate) {
  //     this.selectedRange.endDate = day;
  //   } else {
  //     this.selectedRange = { startDate: day, endDate: null };
  //   }
  // }

  goToPreviousMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.nextMonth = new Date(this.nextMonth.getFullYear(), this.nextMonth.getMonth() - 1, 1);
  }

  goToNextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.nextMonth = new Date(this.nextMonth.getFullYear(), this.nextMonth.getMonth() + 1, 1);
  }

  nextYears(): void {
    this.calendarYear = this.calendarYear + 12;
    this.nextCalStartYear = this.calendarYear + 12;
  }
  previousYears(): void {
    this.calendarYear = this.calendarYear - 11;
    this.nextCalStartYear = this.calendarYear + 12;
  }
  isPastDate(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const inputDate = new Date(date);
    inputDate.setHours(0, 0, 0, 0);
    return inputDate < today;
  }
  clearSelectedDates(): void {
    this.clearDates();
    if (this.hasSingleDatePicker) {
      this.selectedDateChange.emit(this.selectedDate);
    } else {
      this.selectedDates.emit(this.selectedRange);
    }
  }

  clearDates(): void {
    this.selectedDate = null;
    this.selectedRange = { startDate: null, endDate: null };
  }

  applyRange(): void {
    if (this.hasSingleDatePicker) {
      this.selectedDateChange.emit(this.selectedDate);
    } else {
      this.selectedDates.emit(this.selectedRange);
    }
  }

  isApplyDisabled(): boolean {
    if (this.hasSingleDatePicker) {
      return this.selectedDate === null;
    } else {
      this.tooltipText = 'Please select both a Start and End date to apply the filter. For a single-day selection, choose the same date as both Start and End.';
      return this.selectedRange.startDate === null || this.selectedRange.endDate === null;
    }
  }

  getTooltipText(): string {
    if (this.isApplyDisabled()) {
      return this.tooltipText;
    }
    return '';
  }
}
